import { useCallback, useEffect, useState } from 'react'
import { Message } from '@arco-design/web-react'
import {
  getOptionsApi,
  batchUpdateOptionsApi,
  updateOptionApi,
  type BatchUpdateResult,
  type OptionItem,
} from '@/api/option'
import { runOptionAfterSave } from './optionAfterSave'
import { filterSaveableKeys } from './optionMeta'
import { isSensitiveKey } from './optionKeys'

export interface SaveResult {
  savedKeys: string[]
  skippedSensitive: string[]
  failedKeys: Array<{ key: string; error: string }>
}

export interface SaveOptions {
  /** 显式提交敏感字段空值（清空配置） */
  forceSensitive?: boolean
}

export interface OptionsAPI {
  loading: boolean
  saving: boolean
  /** 仅在从服务端完成全量加载后递增；本地 patch 不递增 */
  dataRevision: number
  values: Record<string, string>
  get: (key: string, fallback?: string) => string
  getBool: (key: string, fallback?: boolean) => boolean
  getNumber: (key: string, fallback?: number) => number
  patch: (partial: Record<string, string | number | boolean>) => void
  save: (keys: string[], overrides?: Record<string, string>, options?: SaveOptions) => Promise<SaveResult | null>
  saveOne: (key: string, value: string | number | boolean) => Promise<boolean>
  clearSensitive: (key: string) => Promise<boolean>
  reload: () => Promise<void>
}

function shouldSkipSensitiveSave(key: string, value: string | undefined, opts?: SaveOptions): boolean {
  if (opts?.forceSensitive) return false
  if (!isSensitiveKey(key)) return false
  return value === undefined || String(value).trim() === ''
}

/**
 * 集中管理系统设置的读取/保存，避免各 Tab 重复拉 /api/option。
 */
export function useOptions(): OptionsAPI {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dataRevision, setDataRevision] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const items: OptionItem[] = await getOptionsApi()
      const map: Record<string, string> = {}
      ;(items || []).forEach((it) => {
        map[it.key] = it.value ?? ''
      })
      setValues(map)
      setDataRevision((r) => r + 1)
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const get = useCallback(
    (key: string, fallback = '') => {
      return values[key] !== undefined ? values[key] : fallback
    },
    [values]
  )

  const getBool = useCallback(
    (key: string, fallback = false) => {
      const v = values[key]
      if (v === undefined || v === '') return fallback
      return v === 'true' || v === '1'
    },
    [values]
  )

  const getNumber = useCallback(
    (key: string, fallback = 0) => {
      const v = Number(values[key])
      return Number.isFinite(v) ? v : fallback
    },
    [values]
  )

  const patch = useCallback((partial: Record<string, string | number | boolean>) => {
    setValues((prev) => {
      const next = { ...prev }
      Object.entries(partial).forEach(([k, v]) => {
        next[k] = typeof v === 'string' ? v : String(v)
      })
      return next
    })
  }, [])

  const persistKeys = useCallback(
    async (keys: string[], merged: Record<string, string>, options?: SaveOptions): Promise<SaveResult | null> => {
      const saveableKeys = filterSaveableKeys(keys)
      const payload: Array<{ key: string; value: string }> = []
      const skippedSensitive: string[] = []

      for (const k of saveableKeys) {
        const value = merged[k]
        if (shouldSkipSensitiveSave(k, value, options)) {
          skippedSensitive.push(k)
          continue
        }
        payload.push({ key: k, value: value ?? '' })
      }

      if (payload.length === 0) {
        if (skippedSensitive.length > 0) {
          Message.warning(`敏感字段留空已跳过 ${skippedSensitive.length} 项，无其他变更需保存`)
        } else {
          Message.warning('没有需要保存的变更')
        }
        return null
      }

      const result: BatchUpdateResult = await batchUpdateOptionsApi(payload)

      if (result.failed.length > 0) {
        Message.error(`部分保存失败：${result.failed.map((f) => f.key).join('、')}`)
      } else {
        Message.success('已保存')
      }

      setValues((prev) => {
        const next = { ...prev }
        for (const item of result.saved) {
          next[item.key] = item.value
        }
        return next
      })

      const saveResult: SaveResult = {
        savedKeys: result.saved.map((s) => s.key),
        skippedSensitive,
        failedKeys: result.failed,
      }

      if (saveResult.savedKeys.length > 0) {
        await runOptionAfterSave(saveResult.savedKeys, skippedSensitive)
      }

      return saveResult
    },
    []
  )

  const save = useCallback(
    async (keys: string[], overrides?: Record<string, string>, options?: SaveOptions) => {
      setSaving(true)
      try {
        const merged = { ...values, ...overrides }
        return await persistKeys(keys, merged, options)
      } catch (err: unknown) {
        Message.error(err instanceof Error ? err.message : '保存失败')
        return null
      } finally {
        setSaving(false)
      }
    },
    [values, persistKeys]
  )

  const clearSensitive = useCallback(
    async (key: string) => {
      if (!isSensitiveKey(key)) {
        Message.warning('仅支持清空敏感字段')
        return false
      }
      setSaving(true)
      try {
        const merged = { ...values, [key]: '' }
        const result = await persistKeys([key], merged, { forceSensitive: true })
        if (result && result.savedKeys.includes(key)) {
          Message.success('已清空')
          return true
        }
        return false
      } catch (err: unknown) {
        Message.error(err instanceof Error ? err.message : '清空失败')
        return false
      } finally {
        setSaving(false)
      }
    },
    [values, persistKeys]
  )

  const saveOne = useCallback(
    async (key: string, value: string | number | boolean) => {
      const str = typeof value === 'string' ? value : String(value)
      if (shouldSkipSensitiveSave(key, str)) {
        Message.warning('敏感字段留空，未覆盖已有配置')
        return false
      }
      setSaving(true)
      try {
        await updateOptionApi(key, str)
        setValues((prev) => ({ ...prev, [key]: str }))
        Message.success('已保存')
        await runOptionAfterSave([key], [])
        return true
      } catch (err: unknown) {
        Message.error(err instanceof Error ? err.message : '保存失败')
        return false
      } finally {
        setSaving(false)
      }
    },
    []
  )

  return { loading, saving, dataRevision, values, get, getBool, getNumber, patch, save, saveOne, clearSensitive, reload }
}

export { isSensitiveKey } from './optionKeys'
